import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';
import { getMembers } from '../../../store/team/selectors';
import { getDorChosenDate } from '../../../store/orders/selectors';
import { getSheets } from '../../../store/sheets/selectors';
import { getCommissionPlans, getCommissionTeams } from '../../../store/commissions/selectors';
import { ordersActions } from '../../../store/orders/slice';
import { commissionsActions } from '../../../store/commissions/slice';
import { getIdToken } from '../../../utils/helpers';
import { getOrdersByPermissions, getUsersSummary } from '../../../utils/helpers/commission.helper';
import { useMergeCommission } from '../Shared/hooks';
import { AZURE_FUNCTION_CODE } from '../../../../config';
import { TGetOrdersRes } from '../../../types/responses/orders.type';
import { TAssignOrder, TOrderMapping, TPromoterTotal } from '../../../types';
import { SHOPIFY_AZURE_FUNCTION_URL } from '../../../data';
import { useStats } from '../DailyStats/hooks/useStats';

export const useFetchRemainingOrders = (firstPageData: TGetOrdersRes, ordersMapping: Array<TOrderMapping>) => {
  const dispatch = useAppDispatch();
  const { personnel, shop } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const sheets = useAppSelector(getSheets);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const { mergeCommissionPlansIntoOrders } = useMergeCommission();
  const commissionTeams = useAppSelector(getCommissionTeams);
  const dorChosenDate = useAppSelector(getDorChosenDate);

  const {
    calculateSalesAndCommissions,
    calculateSalesByChannel,
    getOrderTotalsByLocation,
    aggregateDataByLocation,
    ordersByLocation,
    calculateOrdersByMember,
    getStopperSummary,
  } = useStats();

  return useQuery(
    ['remainingOrders', firstPageData],
    async () => {
      if (!firstPageData?.hasNext) {
        return firstPageData?.orders || [];
      }

      let allOrders = [...firstPageData.orders] as Array<TAssignOrder>;
      let workerCompleted = false;

      const worker = new Worker(new URL('./worker_v2.ts', import.meta.url));

      const fetchPromise = new Promise<void>((resolve, reject) => {
        worker.onmessage = (event) => {
          if (event.data.error) {
            reject(event.data.error);
          } else if (event.data.complete) {
            workerCompleted = true;
            resolve();
          } else {
            const orders = mergeCommissionPlansIntoOrders(event.data.orders, ordersMapping);
            const ordersByPermissions = getOrdersByPermissions(personnel, orders);

            allOrders = allOrders.concat(ordersByPermissions);

            const data = getUsersSummary(
              ordersByPermissions,
              members,
              commissionPlans[0],
              sheets,
              commissionTeams,
              shop.shopTimezone,
            );

            const promotersTotals = getStopperSummary(ordersByPermissions);

            dispatch(ordersActions.setPromotersTotals(promotersTotals as unknown as Array<TPromoterTotal>));

            const ordersByMember = calculateOrdersByMember(ordersByPermissions);
            const employeeRates = calculateSalesAndCommissions(ordersByMember);

            dispatch(ordersActions.processBestSalesman(ordersByMember));
            dispatch(ordersActions.processDataByMemberAndLocation(ordersByMember));

            const channelData = calculateSalesByChannel(ordersByPermissions);
            const locationStats = aggregateDataByLocation(ordersByPermissions);

            dispatch(ordersActions.setEmployeeRates(employeeRates));
            dispatch(ordersActions.setChannelData(channelData));
            dispatch(ordersActions.setLocationStats(locationStats));

            const ordersTotals = getOrderTotalsByLocation(ordersByPermissions);
            const orderLocations = ordersByLocation(ordersByPermissions);

            const ordersByDate = ordersByPermissions.filter((order) => {
              const orderDate = moment.tz(order.processedAt, shop?.shopTimezone).startOf('day').format();
              const dorDate = moment.tz(dorChosenDate, shop?.shopTimezone).startOf('day').format();

              return orderDate === dorDate;
            });

            dispatch(commissionsActions.appendToCommissionsSummary(data));
            dispatch(ordersActions.appendAttributedOrders(ordersByPermissions));
            dispatch(ordersActions.mergeOrderTotalsByLocation(ordersTotals));
            dispatch(ordersActions.appendOrdersByDates(ordersByDate));
            dispatch(ordersActions.mergeOrdersByLocation(orderLocations));
          }
        };

        worker.onerror = (error) => {
          reject(error);
          worker.terminate();
        };
      });

      worker.postMessage({
        baseUrl: `${SHOPIFY_AZURE_FUNCTION_URL}/api/getOrdersPage?code=${AZURE_FUNCTION_CODE}`,
        shopName: personnel.shopName,
        encryptedAT: firstPageData.encryptedAT,
        nextLink: firstPageData.nextLink,
        token: getIdToken(''),
      });

      await fetchPromise;

      if (workerCompleted) {
        dispatch(ordersActions.setIsOrdersLoading(false));

        console.log('All pages have been fetched and processed.');
      }
      return allOrders; // Return the final list of all orders fetched
    },
    {
      enabled: !!firstPageData?.hasNext, // Only run if firstPageData has next pages
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      refetchOnReconnect: false,
      staleTime: 3 * (60 * 1000),
    },
  );
};
